import { getApiError } from '../lib/apiError';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Users, Shield, Loader2, UserPlus, Building2, Upload, Trash2, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', password: '', role: 'vendedor' });
  const [companyData, setCompanyData] = useState({
    name: '',
    cnpj: '',
    phone: '',
    email: '',
    address: '',
    pix_key: '',
    logo: ''
  });
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      // Busca dados da empresa
      const companyRes = await axios.get(`${API_URL}/api/company`);
      setCompanyData(companyRes.data);
      
      // Se admin, busca usuários
      if (user?.role === 'admin') {
        const usersRes = await axios.get(`${API_URL}/api/users`);
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/role?role=${newRole}`);
      toast.success('Permissão atualizada!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar permissão');
    }
  };

  const handleCompanySave = async () => {
    setSaving(true);
    try {
      // Não envia o logo junto com os outros dados (é tratado separadamente)
      const { logo, ...dataToSave } = companyData;
      await axios.put(`${API_URL}/api/company`, dataToSave);
      toast.success('Dados da empresa atualizados!');
    } catch (error) {
      toast.error('Erro ao salvar dados da empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use: PNG, JPG ou WEBP');
      return;
    }

    // Validar tamanho (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/company/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setCompanyData({ ...companyData, logo: response.data.logo });
      toast.success('Logo atualizado com sucesso!');
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao fazer upload do logo'));
    } finally {
      setUploadingLogo(false);
      // Limpa o input para permitir reselecionar o mesmo arquivo
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Tem certeza que deseja remover o logo?')) return;

    setUploadingLogo(true);
    try {
      await axios.delete(`${API_URL}/api/company/logo`);
      setCompanyData({ ...companyData, logo: '' });
      toast.success('Logo removido com sucesso!');
    } catch (error) {
      toast.error('Erro ao remover logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteData.name || !inviteData.email || !inviteData.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/invite`, inviteData);
      toast.success('Usuário convidado com sucesso!');
      setInviteDialogOpen(false);
      setInviteData({ name: '', email: '', password: '', role: 'vendedor' });
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao convidar usuário'));
    } finally {
      setSaving(false);
    }
  };

  const roleLabels = {
    admin: 'Administrador',
    vendedor: 'Vendedor',
    operador_caixa: 'Operador(a) de Caixa'
  };

  const roleBadgeColors = {
    admin: 'bg-violet-100 text-violet-800',
    vendedor: 'bg-blue-100 text-blue-800',
    operador_caixa: 'bg-emerald-100 text-emerald-800'
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>
        <p className="text-slate-500 mt-1">Gerencie as configurações do sistema</p>
      </div>

      {/* Company Settings */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <SettingsIcon size={20} className="text-blue-600" />
            Dados da Empresa
          </CardTitle>
          <CardDescription>Informações básicas da sua empresa</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Logo Upload Section */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-sm font-medium flex items-center gap-2 mb-3">
              <Image size={16} className="text-blue-600" />
              Logo da Empresa
            </Label>
            <div className="flex items-center gap-6">
              {/* Logo Preview */}
              <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-white overflow-hidden">
                {companyData.logo ? (
                  <img 
                    src={companyData.logo} 
                    alt="Logo da empresa" 
                    className="w-full h-full object-contain"
                    data-testid="company-logo-preview"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <Image size={32} className="mx-auto mb-1" />
                    <span className="text-xs">Sem logo</span>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1 space-y-3">
                <p className="text-sm text-slate-600">
                  O logo será exibido nos comprovantes, cupons fiscais e DANFE.
                </p>
                <p className="text-xs text-slate-500">
                  Formatos aceitos: PNG, JPG, WEBP • Tamanho máximo: 2MB • Recomendado: 200x200px
                </p>
                <div className="flex gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    data-testid="logo-upload-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    data-testid="logo-upload-btn"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <Upload size={16} className="mr-2" />
                    )}
                    {companyData.logo ? 'Alterar Logo' : 'Enviar Logo'}
                  </Button>
                  {companyData.logo && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogoRemove}
                      disabled={uploadingLogo}
                      className="border-red-500 text-red-600 hover:bg-red-50"
                      data-testid="logo-remove-btn"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="company-name">Nome da Empresa</Label>
              <Input
                id="company-name"
                value={companyData.name}
                onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                data-testid="company-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-cnpj">CNPJ</Label>
              <Input
                id="company-cnpj"
                value={companyData.cnpj}
                onChange={(e) => setCompanyData({ ...companyData, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
                data-testid="company-cnpj"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-phone">Telefone</Label>
              <Input
                id="company-phone"
                value={companyData.phone}
                onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                placeholder="(11) 99999-9999"
                data-testid="company-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-email">Email</Label>
              <Input
                id="company-email"
                type="email"
                value={companyData.email}
                onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                placeholder="contato@empresa.com"
                data-testid="company-email"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="company-address">Endereço</Label>
              <Input
                id="company-address"
                value={companyData.address}
                onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })}
                placeholder="Rua, número, bairro, cidade - UF"
                data-testid="company-address"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="pix-key">Chave PIX para Recebimentos</Label>
              <Input
                id="pix-key"
                value={companyData.pix_key}
                onChange={(e) => setCompanyData({ ...companyData, pix_key: e.target.value })}
                placeholder="email@empresa.com ou CNPJ"
                data-testid="pix-key"
              />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={handleCompanySave} className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-company-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Salvar Configurações
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Management - Admin Only */}
      {user?.role === 'admin' && (
        <Card className="border border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Users size={20} className="text-blue-600" />
                Gerenciamento de Usuários
              </CardTitle>
              <CardDescription>Controle de acesso e permissões da sua empresa</CardDescription>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="invite-user-btn">
                  <UserPlus size={18} className="mr-2" />
                  Convidar Usuário
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Convidar Novo Usuário</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInviteUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      placeholder="Nome do usuário"
                      data-testid="invite-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      placeholder="email@exemplo.com"
                      data-testid="invite-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Senha Inicial</Label>
                    <Input
                      type="password"
                      value={inviteData.password}
                      onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                      placeholder="Senha inicial"
                      data-testid="invite-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Permissão</Label>
                    <Select value={inviteData.role} onValueChange={(v) => setInviteData({ ...inviteData, role: v })}>
                      <SelectTrigger data-testid="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendedor">Vendedor</SelectItem>
                        <SelectItem value="operador_caixa">Operador(a) de Caixa</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-invite-btn">
                      {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                      Convidar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-blue-600" size={32} />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users size={48} className="mx-auto mb-4 text-slate-300" />
                <p>Nenhum usuário encontrado</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Data de Cadastro</TableHead>
                    <TableHead>Permissão</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{new Date(u.created_at).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <Badge className={roleBadgeColors[u.role]}>
                          {roleLabels[u.role]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {u.id !== user.id && (
                          <Select
                            value={u.role}
                            onValueChange={(value) => handleRoleChange(u.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Administrador</SelectItem>
                              <SelectItem value="vendedor">Vendedor</SelectItem>
                              <SelectItem value="operador_caixa">Operador(a) de Caixa</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Profile Info */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Shield size={20} className="text-blue-600" />
            Meu Perfil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={user?.name || ''} readOnly className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} readOnly className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Permissão</Label>
              <Input value={roleLabels[user?.role] || user?.role} readOnly className="bg-slate-50" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
